import { useState } from 'react'
import DiagramFrame from './DiagramFrame'
import DiagramNode from './DiagramNode'
import useApp from '../../models/useApp'
import useNetwork from '../../models/useNetwork'
import useOnboardFrom from '../../hooks/useOnboardFrom'
import useProfile from '../../models/useProfile'
import Typography from '@mui/material/Typography'
import NextPrev from './NextPrev'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { Link } from 'react-router-dom'

export default function OnboardWelcome() {
  const { networks } = useNetwork()
  const { ready } = useApp()
  const { fullName, save, set } = useProfile()
  const [name, setName] = useState('')
  // Exit is the other way out of the wizard, and it owes the same return as Done.
  const from = useOnboardFrom()

  // The only place the app ever asks. WorkOS Magic Auth collects an email and
  // nothing else, so a third of profiles carry no name at all, and the router only
  // composes one when it *creates* the profile. Empty arrives as '' as often as
  // NULL, hence the trim rather than a null check.
  //
  // `ready` is not decoration: AuthGuard renders this route as soon as the session
  // initializes, which is well before controller.loadState() has fetched the
  // profile — and this URL is landable, since the redirect puts it in the address
  // bar. Without the gate a reload asks a named user for a name they already have,
  // and typing one overwrites it.
  const needsName = ready && !fullName?.trim()

  // Skippable on purpose: Begin stays enabled and an empty box saves nothing. The
  // wizard is a one-shot — once a node exists it never fires again — so the second
  // ask is the profile page's, which nudges for a name whenever it is blank.
  const onNext = async () => {
    const value = name.trim()
    if (!value) return
    await save({ fullName: value })
    // ⚠ And into the STORE, or the next step's Back button asks again. `save` writes
    // to Postgres and refetches the org labels, but never touches local state —
    // Profile.tsx gets away with that because it `set`s on every keystroke, which
    // this cannot do: the field is gated on `needsName`, so binding it to the store
    // would make it vanish on the first character typed. Hence a local box and an
    // explicit set here. Without it `fullName` stays '' for the life of the page,
    // `needsName` stays true, and stepping back to Welcome (Network/Node both point
    // `prev` here) re-asks a user who just answered — with an empty field, since the
    // local state died with the unmount.
    set({ fullName: value })
  }

  return (
    <>
      <Grid size={{ xs: 12, sm: 3.5 }} />
      <Grid size={{ xs: 12, sm: 8.5 }}>
        <Typography variant="h1">Welcome to noBGP</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 3.5 }} sx={{
        paddingTop: 4
      }}>
        <Typography variant="h5">noBGP allows your LLM to connect to compute nodes.</Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: 1
          }}>
          Let&apos;s get started.
        </Typography>
        <Typography variant="caption" sx={{
          color: 'text.secondary'
        }}>
          Your networks and nodes belong to your organization — invite teammates anytime from the account
          menu.
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8.5 }}>
        <DiagramFrame
          left={<DiagramNode label="Language Model" type="llm" />}
          right={<DiagramNode label="Compute Node" type="node" />}
        />
      </Grid>
      {needsName && (
        <>
          <Grid size={{ xs: 12, sm: 3.5 }} sx={{
            paddingTop: 4
          }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                marginBottom: 3
              }}>
              What should we call you? Your name is how teammates see you in shared networks.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8.5 }} sx={{
            paddingTop: 4
          }}>
            <TextField
              autoFocus
              fullWidth
              label="Your name"
              id="full-name"
              type="text"
              autoComplete="name"
              value={name}
              helperText="You can change this any time on your account page."
              onChange={e => setName(e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid
        size={12}
        sx={{
          marginTop: 4,
          textAlign: 'right'
        }}>
        <Button
          to={from ?? '/'}
          state={from?.state}
          size="small"
          component={Link}
          sx={{ color: 'contrast.medium', mr: 1 }}
        >
          Exit
        </Button>
        <NextPrev
          next={networks.length ? '/onboard/node' : '/onboard/network'}
          label="Begin"
          onNext={needsName ? onNext : undefined}
        />
      </Grid>
    </>
  )
}
